import { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { Spinner } from '@/components/Spinner';
import { TableProps, TableColumn, SortOrder } from '@/types/table';
import { ActionsCell } from './ActionsCell';
import { TableSearch } from './TableSearch';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { Pagination } from '@/components/Pagination';
import { useColumnWidths } from './useColumnWidths';
import { cn } from '@/lib/utils';

const INTERSECTION_THRESHOLD = 0.8;

const LoadMoreTrigger = ({
    loadMoreRef,
    isFetching,
}: {
    loadMoreRef: React.RefObject<HTMLDivElement | null>;
    isFetching: boolean;
}) => (
    <div
        ref={loadMoreRef}
        className="h-16 flex items-center justify-center border-t border-grey-500"
    >
        {isFetching ? (
            <Spinner />
        ) : (
            <span className="text-grey-300 text-sm">Load more...</span>
        )}
    </div>
);

export function Table<TData>(props: TableProps<TData>) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const {
        data,
        columns,
        actions,
        isLoading = false,
        isFetching = false,
        page = 1,
        pageSize = 20,
        totalCount,
        search = '',
        sortBy,
        sortOrder = SortOrder.ASC,
        skeletonRowCount = 5,
        skeletonTypeMap,
        emptyState,
        onLoadMore,
        onPageChange,
        onRowClick,
        onSearchChange,
        onSortChange,
        inlineEditConfig,
        resizableColumns = false,
        expandableRow,
    } = props;

    const { widths: columnWidths, startResize } = useColumnWidths();

    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    useEffect(() => setExpandedKey(null), [page]);
    const toggleExpand = useCallback((key: string) => {
        setExpandedKey((prev) => (prev === key ? null : key));
    }, []);

    const allColumns = useMemo(() => {
        if (!actions) return columns;

        return [
            ...columns,
            {
                id: '_actions',
                header: '',
                width: '60px',
                interactive: true,
                cell: ({
                    row,
                }: {
                    row: { original: TData };
                    value: unknown;
                }) => (
                    <ActionsCell
                        row={row.original}
                        actions={actions(row.original)}
                    />
                ),
            } as TableColumn<TData>,
        ];
    }, [columns, actions]);

    const features = useMemo(
        () => ({
            hasSearch: onSearchChange !== undefined,
            hasPagination:
                onPageChange !== undefined &&
                totalCount !== undefined &&
                totalCount > pageSize,
            hasMore: totalCount ? page * pageSize < totalCount : false,
        }),
        [onSearchChange, onPageChange, totalCount, page, pageSize],
    );

    const tableLayout = useMemo(
        () =>
            resizableColumns || allColumns.some((col) => col.width)
                ? 'table-fixed'
                : 'table-auto',
        [allColumns, resizableColumns],
    );

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            if (entries[0].isIntersecting && !isFetching && onLoadMore) {
                onLoadMore();
            }
        },
        [isFetching, onLoadMore],
    );

    useEffect(() => {
        if (!onLoadMore || !loadMoreRef.current) return;

        const observer = new IntersectionObserver(handleIntersection, {
            threshold: INTERSECTION_THRESHOLD,
            root: scrollContainerRef.current,
        });

        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [onLoadMore, handleIntersection]);

    const isEmptyWithEmptyState = data.length === 0 && emptyState && !isLoading;

    return (
        <div
            className={cn(
                'w-full',
                !isEmptyWithEmptyState && 'space-y-4',
                isEmptyWithEmptyState && 'h-full',
            )}
        >
            {features.hasSearch && onSearchChange && (
                <TableSearch
                    search={search}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    onSearchChange={onSearchChange}
                />
            )}
            <div
                className={cn(
                    'rounded-[8px] border border-grey-500 bg-grey-700 relative overflow-hidden',
                    isEmptyWithEmptyState && 'h-full',
                )}
            >
                <div
                    ref={scrollContainerRef}
                    className={cn(
                        'relative w-full overflow-auto',
                        isEmptyWithEmptyState && 'h-full',
                    )}
                >
                    <table
                        className={cn(
                            'w-full min-w-full caption-bottom text-sm',
                            tableLayout,
                            isEmptyWithEmptyState && 'h-full',
                        )}
                    >
                        <TableHeader
                            columns={allColumns}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSortChange={onSortChange}
                            columnWidths={
                                resizableColumns ? columnWidths : undefined
                            }
                            onStartResize={
                                resizableColumns ? startResize : undefined
                            }
                        />
                        <TableBody
                            data={data}
                            columns={allColumns}
                            isLoading={isLoading}
                            onRowClick={onRowClick}
                            page={page}
                            inlineEditConfig={inlineEditConfig}
                            skeletonRowCount={skeletonRowCount}
                            skeletonTypeMap={skeletonTypeMap}
                            emptyState={emptyState}
                            expandableRow={expandableRow}
                            expandedKey={expandedKey}
                            onToggleExpand={toggleExpand}
                        />
                    </table>

                    {onLoadMore && features.hasMore && (
                        <LoadMoreTrigger
                            loadMoreRef={loadMoreRef}
                            isFetching={isFetching}
                        />
                    )}
                </div>
            </div>
            {features.hasPagination && !onLoadMore && (
                <div className="flex justify-center">
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount!}
                        isLoading={isLoading}
                        hasMore={features.hasMore}
                        onPageChange={onPageChange!}
                    />
                </div>
            )}
        </div>
    );
}
